import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Paper,
  TablePagination,
  CircularProgress,
  Alert,
  Tooltip,
  IconButton,
} from "@mui/material";
import {
  Receipt as ReceiptIcon,
  CheckCircleOutline as ApprovedIcon,
  CancelOutlined as RejectedIcon,
  AccessTime as PendingIcon,
  Info as InfoIcon,
} from "@mui/icons-material";
import { format } from "date-fns";
import api from "../../utils/api";

const SolarTransactionHistory = () => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  useEffect(() => {
    const fetchTransactions = async () => {
      try {
        setLoading(true);
        const response = await api.get("/solar-program/transactions/my");
        if (response.data.status === "success") {
          setTransactions(response.data.data.transactions || []);
        }
        setError(null);
      } catch (err) {
        setError(
          err.response?.data?.message || "Failed to fetch transaction history"
        );
        // console.error("Solar transactions fetch error:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchTransactions();
  }, []);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getStatusChip = (status, approvedAt, rejectionReason) => {
    switch (status) {
      case "approved":
        return (
          <Tooltip
            title={
              approvedAt
                ? `Approved on ${format(new Date(approvedAt), "MMM dd, yyyy")}`
                : ""
            }
          >
            <Chip
              icon={<ApprovedIcon />}
              label="Approved"
              color="success"
              size="small"
            />
          </Tooltip>
        );
      case "rejected":
        return (
          <Tooltip title={rejectionReason || "Transaction rejected"}>
            <Chip
              icon={<RejectedIcon />}
              label="Rejected"
              color="error"
              size="small"
            />
          </Tooltip>
        );
      default:
        return (
          <Chip
            icon={<PendingIcon />}
            label="Pending"
            color="warning"
            size="small"
          />
        );
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          mb: 3,
        }}
      >
        <ReceiptIcon sx={{ mr: 1, color: "primary.main" }} />
        <Typography variant="h5">Solar Transaction History</Typography>
      </Box>

      <Card>
        <CardContent>
          {loading ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          ) : transactions.length === 0 ? (
            <Alert severity="info">
              You don't have any solar program transactions yet. Make a deposit
              to get started with the solar program.
            </Alert>
          ) : (
            <>
              <TableContainer component={Paper} variant="outlined">
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>Reference</TableCell>
                      <TableCell align="right">Amount</TableCell>
                      <TableCell align="center">Status</TableCell>
                      <TableCell>Details</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {transactions
                      .slice(
                        page * rowsPerPage,
                        page * rowsPerPage + rowsPerPage
                      )
                      .map((transaction) => (
                        <TableRow key={transaction._id}>
                          <TableCell>
                            {format(
                              new Date(transaction.createdAt),
                              "MMM dd, yyyy"
                            )}
                          </TableCell>
                          <TableCell>
                            {transaction.transactionReference}
                          </TableCell>
                          <TableCell align="right">
                            ₦{transaction.amount?.toLocaleString() || "6,000"}
                          </TableCell>
                          <TableCell align="center">
                            {getStatusChip(
                              transaction.status,
                              transaction.approvedAt,
                              transaction.rejectionReason
                            )}
                          </TableCell>
                          <TableCell>
                            <Tooltip title="Transaction Details">
                              <IconButton size="small" color="primary">
                                <InfoIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <TablePagination
                component="div"
                count={transactions.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[5, 10, 25]}
              />

              <Box sx={{ mt: 2 }}>
                <Alert severity="info" icon={false}>
                  <Typography variant="subtitle2">
                    Understanding Transaction Status:
                  </Typography>
                  <Box component="ul" sx={{ pl: 2, mt: 1, mb: 0 }}>
                    <li>
                      <Typography variant="body2">
                        <strong>Pending</strong>: Your deposit notification has
                        been received and is awaiting verification.
                      </Typography>
                    </li>
                    <li>
                      <Typography variant="body2">
                        <strong>Approved</strong>: Your deposit has been
                        verified and approved.
                      </Typography>
                    </li>
                    <li>
                      <Typography variant="body2">
                        <strong>Rejected</strong>: Your deposit notification
                        couldn't be verified. Hover over the status for details.
                      </Typography>
                    </li>
                  </Box>
                </Alert>
              </Box>
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default SolarTransactionHistory;
